// Translates between the widget config the API speaks and the flat `settings`
// object these cards were built against.
//
// PATCH /assistants/:id
//   { name, tagline, allowedDomain, widgetTheme: { ...flat fields... } }
//
// widgetTheme is a single flat object — colours, typography, launcher, layout
// and behaviour all sit side by side, and welcomeMessage / suggestedPrompts
// live inside it too (they used to be top-level).
//
// Reading goes through readWidgetTheme, which normalises whichever shape the
// API sends; writing always emits the flat one.

import { readWidgetTheme } from "../../utils/widgetTheme";
import {
  CUSTOM_POSITION,
  isCustomPosition,
  normalizeOffset,
  DEFAULT_CUSTOM_OFFSET,
} from "./widgetPlacement";

// The cards use short ids; the API spells the launcher and position out.
const LAUNCHER_TO_API = { bubble: "chat-bubble" };
const LAUNCHER_FROM_API = { "chat-bubble": "bubble" };
const POSITION_TO_API = { right: "bottom-right", left: "bottom-left" };
const POSITION_FROM_API = { "bottom-right": "right", "bottom-left": "left" };

// The card offers Regular/Medium/Bold; the API's lightest weight is "normal",
// so this one can't just be lowercased.
const NAME_STYLE_TO_API = { Regular: "normal", Medium: "medium", Bold: "bold" };
const NAME_STYLE_FROM_API = {
  normal: "Regular",
  regular: "Regular",
  medium: "Medium",
  bold: "Bold",
};

// "Minimized" / "Expanded" in the card, "minimized" / "open" over the wire.
const INITIAL_STATE_TO_API = { Minimized: "minimized", Expanded: "open" };
const INITIAL_STATE_FROM_API = { minimized: "Minimized", open: "Expanded" };

const toTitle = (value) =>
  typeof value === "string" && value
    ? value.charAt(0).toUpperCase() + value.slice(1)
    : value;

const toLower = (value) =>
  typeof value === "string" ? value.toLowerCase() : value;

// Only overwrite a default when the API actually sent something, so a partial
// config doesn't blank out fields the cards need populated.
const pick = (value, fallback) =>
  value === undefined || value === null ? fallback : value;

// The card offers one dropdown; the API splits it into a flag and a number.
const autoOpenLabel = (autoOpen, seconds, fallback) => {
  if (autoOpen === false) return "Never";
  if (typeof seconds !== "number") return fallback;
  return seconds === 0 ? "Immediately" : `${seconds} seconds`;
};

const autoOpenToApi = (label) => {
  if (label === "Never") return { autoOpen: false, autoOpenAfterSeconds: 0 };
  if (label === "Immediately") return { autoOpen: true, autoOpenAfterSeconds: 0 };

  const seconds = parseInt(label, 10);
  return {
    autoOpen: true,
    autoOpenAfterSeconds: Number.isNaN(seconds) ? 0 : seconds,
  };
};

export const configToSettings = (config, defaults) => {
  const theme = readWidgetTheme(config);

  return {
    ...defaults,

    primaryColor: pick(theme.primaryColor, defaults.primaryColor),
    headerColor: pick(theme.headerColor, defaults.headerColor),
    accentColor: pick(theme.accentColor, defaults.accentColor),
    buttonColor: pick(theme.buttonColor, defaults.buttonColor),

    fontFamily: pick(theme.fontFamily, defaults.fontFamily),
    fontSize: pick(theme.fontSize, defaults.fontSize),
    messageSize: toTitle(pick(theme.messageSize, defaults.messageSize)),
    nameStyle:
      NAME_STYLE_FROM_API[toLower(theme.assistantNameStyle)] ||
      defaults.nameStyle,

    launcherStyle: theme.launcherStyle
      ? LAUNCHER_FROM_API[theme.launcherStyle] || theme.launcherStyle
      : defaults.launcherStyle,
    avatarImage: pick(theme.avatarUrl, defaults.avatarImage),

    widgetPosition: theme.position
      ? POSITION_FROM_API[theme.position] || theme.position
      : defaults.widgetPosition,
    // Read whatever the record holds, even alongside a corner position, so
    // switching back to Custom returns to the point last placed rather than
    // to the default corner.
    widgetOffsetX: normalizeOffset(
      theme.positionOffsetX,
      defaults.widgetOffsetX,
    ),
    widgetOffsetY: normalizeOffset(
      theme.positionOffsetY,
      defaults.widgetOffsetY,
    ),
    borderRadius: pick(theme.borderRadius, defaults.borderRadius),
    bubbleStyle: pick(theme.bubbleStyle, defaults.bubbleStyle),
    theme: pick(theme.colorScheme, defaults.theme),
    spacing: pick(theme.spacing, defaults.spacing),

    initialState:
      INITIAL_STATE_FROM_API[toLower(theme.initialState)] ||
      defaults.initialState,
    autoOpenAfter: autoOpenLabel(
      theme.autoOpen,
      theme.autoOpenAfterSeconds,
      defaults.autoOpenAfter,
    ),
    showWelcomeMessage: pick(
      theme.showWelcomeMessage,
      defaults.showWelcomeMessage,
    ),
    showAvatar: pick(theme.showAvatar, defaults.showAvatar),
    allowClose: pick(theme.allowUserToCloseWidget, defaults.allowClose),
    showPoweredBy: pick(theme.showPoweredBy, defaults.showPoweredBy),
    showOnlineStatus: pick(theme.showOnlineStatus, defaults.showOnlineStatus),
    isAuthorizationRequired: pick(
      theme.isAuthorizationRequired,
      defaults.isAuthorizationRequired,
    ),

    name: pick(config?.name, defaults.name),
    welcomeMessage: pick(theme.welcomeMessage, defaults.welcomeMessage),
    quickActions: pick(theme.suggestedPrompts, defaults.quickActions),
  };
};

/**
 * Builds the PATCH body, to exactly the documented shape. Keys are written out
 * explicitly rather than spread from the fetched config, so nothing outside
 * that shape can reach a validating endpoint. PATCH is partial, so fields this
 * page doesn't edit (tagline, allowedDomain) are left out.
 *
 * @param settings the flat card state
 */
export const settingsToPayload = (settings) => {
  const autoOpen = autoOpenToApi(settings.autoOpenAfter);

  return {
    name: settings.name?.trim(),
    widgetTheme: {
      primaryColor: settings.primaryColor,
      headerColor: settings.headerColor,
      accentColor: settings.accentColor,
      buttonColor: settings.buttonColor,

      fontFamily: settings.fontFamily,
      fontSize: settings.fontSize,
      messageSize: toLower(settings.messageSize),
      assistantNameStyle:
        NAME_STYLE_TO_API[settings.nameStyle] || toLower(settings.nameStyle),

      launcherStyle:
        LAUNCHER_TO_API[settings.launcherStyle] || settings.launcherStyle,
      avatarUrl: settings.avatarImage,

      // A custom placement sends the literal "custom" and the point with it.
      // The offsets ride along only in that mode, so a corner placement still
      // sends exactly the fields it always did.
      position: isCustomPosition(settings.widgetPosition)
        ? CUSTOM_POSITION
        : POSITION_TO_API[settings.widgetPosition] || settings.widgetPosition,
      ...(isCustomPosition(settings.widgetPosition)
        ? {
            positionOffsetX: normalizeOffset(
              settings.widgetOffsetX,
              DEFAULT_CUSTOM_OFFSET.x,
            ),
            positionOffsetY: normalizeOffset(
              settings.widgetOffsetY,
              DEFAULT_CUSTOM_OFFSET.y,
            ),
          }
        : null),
      borderRadius: settings.borderRadius,
      bubbleStyle: settings.bubbleStyle,
      colorScheme: settings.theme,
      spacing: settings.spacing,

      initialState:
        INITIAL_STATE_TO_API[settings.initialState] ||
        toLower(settings.initialState),
      autoOpen: autoOpen.autoOpen,
      autoOpenAfterSeconds: autoOpen.autoOpenAfterSeconds,

      showWelcomeMessage: settings.showWelcomeMessage,
      welcomeMessage: settings.welcomeMessage,
      showAvatar: settings.showAvatar,
      allowUserToCloseWidget: settings.allowClose,
      suggestedPrompts: settings.quickActions,

      isAuthorizationRequired: settings.isAuthorizationRequired,
      showPoweredBy: settings.showPoweredBy,
      showOnlineStatus: settings.showOnlineStatus,
    },
  };
};
